import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import { BadgeCheck, SquareCheck, SquareX } from "lucide-react";

export type IncludeExcludeValue = "include" | "exclude" | "mustInclude";

interface IncludeExcludeToggleProps {
  /** Undefined: nothing selected yet. */
  value: IncludeExcludeValue | undefined;
  onChange: (value: IncludeExcludeValue) => void;
  className?: string;
  /**
   * `pill` is Include and Exclude as a small round pill with no track fill,
   * for sitting at the end of a settings row.
   */
  variant?: "two-state" | "three-state" | "pill" | "footer";
}

const PILL_ACTIVE: Record<IncludeExcludeValue, string> = {
  mustInclude: "bg-sky-800 text-white",
  include: "bg-emerald-800 text-white",
  exclude: "bg-red-800 text-white",
};

export const IncludeExcludeToggle = ({
  value,
  onChange,
  className,
  variant = "two-state",
}: IncludeExcludeToggleProps) => {
  const { t } = useTranslation();
  const label: Record<IncludeExcludeValue, string> = {
    mustInclude: t("mustInclude"),
    include: t("include"),
    exclude: t("exclude"),
  };

  if (variant === "footer") {
    const segments: IncludeExcludeValue[] = ["include", "exclude"];
    return (
      <div
        role="radiogroup"
        className={cn(
          "grid w-full grid-cols-2 border-t border-neutral-700",
          className,
        )}
      >
        {segments.map((segment, i) => (
          <button
            key={segment}
            type="button"
            role="radio"
            aria-checked={value === segment}
            onClick={() => onChange(segment)}
            className={cn(
              "py-2 text-xs font-semibold transition-colors",
              i > 0 && "border-l border-neutral-700",
              value === segment
                ? PILL_ACTIVE[segment]
                : "text-foreground-muted hover:text-white",
            )}
          >
            {label[segment]}
          </button>
        ))}
      </div>
    );
  }

  if (variant === "pill") {
    const segments: IncludeExcludeValue[] = ["include", "exclude"];
    return (
      <div
        role="radiogroup"
        className={cn(
          "inline-flex items-center gap-0.5 rounded-full border border-neutral-700 p-0.5",
          className,
        )}
      >
        {segments.map((segment) => (
          <button
            key={segment}
            type="button"
            role="radio"
            aria-checked={value === segment}
            onClick={() => onChange(segment)}
            className={cn(
              "whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold transition-colors",
              value === segment
                ? PILL_ACTIVE[segment]
                : "text-foreground-muted hover:text-white",
            )}
          >
            {label[segment]}
          </button>
        ))}
      </div>
    );
  }

  if (variant === "three-state") {
    return (
      <div
        className={cn(
          "flex gap-1 p-1 rounded-lg bg-new-gray-750 border border-panel-foreground shadow-button-new w-full mobile:w-80",
          className,
        )}
      >
        <button
          onClick={() => onChange("mustInclude")}
          className={cn(
            "flex-1 flex flex-col items-center justify-center px-2 py-1.5 rounded text-sm transition-colors cursor-pointer whitespace-nowrap",
            value === "mustInclude"
              ? "bg-sky-800 text-sky-100"
              : "text-dark-tertiary hover:bg-panel-foreground/50",
          )}
        >
          {/* <BadgeCheck size={18} /> */}
          <span>{t("mustInclude")}</span>
        </button>
        <button
          onClick={() => onChange("include")}
          className={cn(
            "flex-1 flex flex-col items-center justify-center px-2 py-1.5 rounded text-sm transition-colors cursor-pointer",
            value === "include"
              ? "bg-emerald-800 text-emerald-100"
              : "text-dark-tertiary hover:bg-panel-foreground/50",
          )}
        >
          {/* <SquareCheck size={18} /> */}
          <span>{t("include")}</span>
        </button>
        <button
          onClick={() => onChange("exclude")}
          className={cn(
            "flex-1 flex flex-col items-center justify-center px-2 py-1.5 rounded text-sm transition-colors cursor-pointer",
            value === "exclude"
              ? "bg-red-800 text-red-100"
              : "text-dark-tertiary hover:bg-panel-foreground/50",
          )}
        >
          {/* <SquareX size={18} /> */}
          <span>{t("exclude")}</span>
        </button>
      </div>
    );
  }

  return (
    <div className={cn("flex gap-x-2 p-1 rounded-lg", className)}>
      <button
        onClick={() => onChange("include")}
        className={cn(
          "flex-1 flex items-center justify-center px-2 py-1.5 rounded text-sm transition-colors whitespace-nowrap bg-panel-foreground shadow-button-new",
          value === "include"
            ? "bg-emerald-800 text-white"
            : "text-dark-secondary hover:bg-panel-highlight",
        )}
      >
        {t("include")}
      </button>
      <button
        onClick={() => onChange("exclude")}
        className={cn(
          "flex-1 flex items-center justify-center px-2 py-1.5 rounded text-sm transition-colors whitespace-nowrap bg-panel-foreground shadow-button-new",
          value === "exclude"
            ? "bg-red-800 text-white"
            : "text-dark-secondary hover:bg-panel-highlight",
        )}
      >
        {t("exclude")}
      </button>
    </div>
  );
};
